import { useMemo, useState } from 'react';
import { Alert, Button, Select, Space, Spin, Typography, message } from 'antd';
import { LinkOutlined, SearchOutlined, DisconnectOutlined } from '@ant-design/icons';
import debounce from 'lodash/debounce';
import { useClientApi, ClientSearchResult } from 'api/useClient';
import { useLinkPartnerClient, useUnlinkPartnerClient } from 'api/partnerAclAdmin';
import { PartnerAclPartner } from 'types/PartnerAclAdmin';

interface PartnerAclClientLinkPanelProps {
  partner: PartnerAclPartner;
  canUpdate: boolean;
}

const PartnerAclClientLinkPanel: React.FC<PartnerAclClientLinkPanelProps> = ({ partner, canUpdate }) => {
  const { searchClientsByName } = useClientApi();
  const { mutateAsync: linkClient, isPending: isLinking } = useLinkPartnerClient(partner.id);
  const { mutateAsync: unlinkClient, isPending: isUnlinking } = useUnlinkPartnerClient(partner.id);

  const [searchLoading, setSearchLoading] = useState(false);
  const [results, setResults] = useState<ClientSearchResult[]>([]);
  const [selectedClientId, setSelectedClientId] = useState<number>();

  const handleSearch = useMemo(
    () =>
      debounce(async (name: string) => {
        if (name.length < 2) {
          setResults([]);
          return;
        }
        setSearchLoading(true);
        const res = await searchClientsByName(name);
        setResults(Array.isArray(res) ? res : []);
        setSearchLoading(false);
      }, 325),
    [searchClientsByName]
  );

  const handleLink = async () => {
    if (!selectedClientId) {
      return;
    }
    await linkClient(selectedClientId);
    message.success('Linked Vantage client to partner.');
    setSelectedClientId(undefined);
    setResults([]);
  };

  const handleUnlink = async () => {
    await unlinkClient();
    message.success('Unlinked Vantage client from partner.');
  };

  return (
    <Space direction="vertical" size="middle" className="w-full">
      <Alert
        type="info"
        showIcon
        message="Human OAuth MCP login"
        description="Link a Vantage client so users on that client authenticate as this partner for MCP. Create and manage those users in Client Admin — not here."
      />

      {partner.linked_client ? (
        <div>
          <Typography.Text strong>Linked client</Typography.Text>
          <div>
            <Typography.Text>{partner.linked_client.name ?? 'Unnamed client'}</Typography.Text>
            <Typography.Text type="secondary" className="ml-2">
              (ID {partner.linked_client.id})
            </Typography.Text>
          </div>
          {canUpdate && (
            <Button danger icon={<DisconnectOutlined />} className="mt-3" loading={isUnlinking} onClick={handleUnlink}>
              Unlink client
            </Button>
          )}
        </div>
      ) : (
        canUpdate && (
          <div>
            <Typography.Paragraph type="secondary" className="mb-2">
              Search for a Vantage client to link. Each client can be linked to at most one partner.
            </Typography.Paragraph>
            <Select<number, ClientSearchResult>
              showSearch
              allowClear
              size="large"
              placeholder="Search by client name"
              style={{ width: '100%', maxWidth: 480 }}
              filterOption={false}
              suffixIcon={<SearchOutlined />}
              notFoundContent={searchLoading ? <Spin size="small" /> : null}
              fieldNames={{ label: 'name', value: 'id' }}
              options={results}
              value={selectedClientId}
              onSearch={handleSearch}
              onChange={(value) => setSelectedClientId(value)}
              onClear={() => {
                setSelectedClientId(undefined);
                setResults([]);
              }}
            />
            <Button
              type="primary"
              icon={<LinkOutlined />}
              className="mt-3"
              disabled={!selectedClientId}
              loading={isLinking}
              onClick={handleLink}
            >
              Link client
            </Button>
          </div>
        )
      )}
    </Space>
  );
};

export default PartnerAclClientLinkPanel;
